import { inject } from 'aurelia-framework';
import { Setup } from 'startup/setup';
import { AuthService } from 'pages/login/service';
import { FavoriteService } from 'resources/elements/favorite/service';

import 'bootstrap';
import 'styles/styles.scss';
import 'toastr/build/toastr.css';
import 'tippy.js/dist/tippy.css';
import 'filepond/dist/filepond.min.css';

@inject(Setup, AuthService, FavoriteService)
export class App {
    constructor(setup, authService, favoriteService) {
        setup.configureFetch();
        this.authService = authService;
        this.favoriteService = favoriteService;
    }

    async activate() {
        const params = new URLSearchParams(window.location.search);
        this.isPrint = params.get('print') == 'true';
        await this.favoriteService.loadFavorites();
        await this.authService.getRole();
    }

    async attached() {
        Waves.init();
    }

    configureRouter(config, router) {
        this.router = router;
        config.title = 'Quote DB';
        config.options.pushState = true;
        config.map([
            {
                route: '',
                name: 'dashboard',
                title: 'Dashboard',
                moduleId: PLATFORM.moduleName('pages/dashboard/index')
            },
            {
                route: 'quotes',
                name: 'quotes',
                title: 'Work with Quotes',
                moduleId: PLATFORM.moduleName('pages/quotes/index')
            },
            {
                route: 'quote/edit/:id',
                name: 'quote-edit',
                title: 'Edit Quotation',
                moduleId: PLATFORM.moduleName('pages/quotes/edit/index')
            },
            {
                route: 'quote/print/:id?',
                name: 'quote-print',
                title: 'Print Quotation',
                moduleId: PLATFORM.moduleName('pages/quotes/print/index')
            },
            {
                route: 'quote-setup/plants',
                name: 'plants',
                title: 'Plants',
                moduleId: PLATFORM.moduleName('pages/quote-setup/plants/index')
            },
            {
                route: 'system-fields/statuses',
                name: 'statuses',
                title: 'Statuses',
                moduleId: PLATFORM.moduleName('pages/system-fields/statuses/index')
            },
            {
                route: 'daiatari/assigned-projects',
                name: 'assigned-projects',
                title: 'Assigned Projects',
                moduleId: PLATFORM.moduleName('pages/daiatari/assigned-projects/index')
            },
            {
                route: 'daiatari/assigned-projects/link/:id',
                name: 'link-assigned-project',
                title: 'Link Quotes to Assigned Projects',
                parent: 'assigned-projects',
                moduleId: PLATFORM.moduleName('pages/daiatari/assigned-projects/link-to-quote/index')
            },
            {
                route: 'daiatari/report',
                name: 'daiatari-report',
                title: 'Daiatari Report',
                moduleId: PLATFORM.moduleName('pages/daiatari/report/index')
            },
            {
                route: 'daiatari/report/html',
                name: 'daiatari-report-html',
                title: 'Daiatari HTML Report',
                moduleId: PLATFORM.moduleName('pages/daiatari/report/html-report/index')
            },
            {
                route: 'sales-forecast',
                name: 'sales-forecast',
                title: 'Sales Forecast',
                moduleId: PLATFORM.moduleName('pages/sales-forecast/index')
            },
            {
                route: 'quote-setup/customers',
                name: 'customers',
                title: 'Customers',
                moduleId: PLATFORM.moduleName('pages/quote-setup/customers/index')
            },
            {
                route: 'system-fields/coatings',
                name: 'coatings',
                title: 'Coatings',
                moduleId: PLATFORM.moduleName('pages/system-fields/coatings/index')
            },
            {
                route: 'system-fields/commodities',
                name: 'commodities',
                title: 'Commodities',
                moduleId: PLATFORM.moduleName('pages/system-fields/commodities/index')
            },
            {
                route: 'system-fields/materials',
                name: 'materials',
                title: 'Materials',
                moduleId: PLATFORM.moduleName('pages/system-fields/materials/index')
            },
            {
                route: 'system-fields/snapshot-types',
                name: 'snapshot-types',
                title: 'Snapshot Types',
                moduleId: PLATFORM.moduleName('pages/system-fields/snapshot-types/index')
            },
            {
                route: 'system-fields/purchase-part-types',
                name: 'purchase-part-types',
                title: 'Purchase Part Types',
                moduleId: PLATFORM.moduleName('pages/system-fields/purchase-part-types/index')
            },
            {
                route: 'system-fields/specific-gravities',
                name: 'specific-gravities',
                title: 'Specific Gravities',
                moduleId: PLATFORM.moduleName('pages/system-fields/specific-gravities/index')
            },
            {
                route: 'standard-setup/processes',
                name: 'processes',
                title: 'Processes',
                moduleId: PLATFORM.moduleName('pages/standard-setup/processes/index')
            },
            {
                route: 'system-fields/stages',
                name: 'stages',
                title: 'Stages',
                moduleId: PLATFORM.moduleName('pages/system-fields/stages/index')
            },
            {
                route: 'system-fields/unit-of-measurements',
                name: 'unit-of-measurements',
                title: 'Unit Of Measurements',
                moduleId: PLATFORM.moduleName('pages/system-fields/unit-of-measurements/index')
            },
            {
                route: 'quote-setup/projects',
                name: 'projects',
                title: 'Projects',
                moduleId: PLATFORM.moduleName('pages/quote-setup/projects/index')
            },
            {
                route: 'standard-setup/customer-projects',
                name: 'customer-projects',
                title: 'Customer Projects',
                moduleId: PLATFORM.moduleName('pages/standard-setup/customer-projects/index')
            },
            {
                route: 'standard-setup/part-sizes',
                name: 'part-sizes',
                title: 'Part Sizes',
                moduleId: PLATFORM.moduleName('pages/standard-setup/part-sizes/index')
            },
            {
                route: 'standard-setup/machines',
                name: 'machines',
                title: 'Machines',
                moduleId: PLATFORM.moduleName('pages/standard-setup/machines/index')
            },
            {
                route: 'quote-setup/parts',
                name: 'parts',
                title: 'Parts',
                moduleId: PLATFORM.moduleName('pages/quote-setup/parts/index')
            },
            {
                route: 'quote-setup/purchase-type-rules',
                name: 'purchase-type-rules',
                title: 'Purchase Type Rules',
                moduleId: PLATFORM.moduleName('pages/quote-setup/purchase-type-rules/index')
            },
            {
                route: 'quote-setup/purchase-item-prices',
                name: 'purchase-item-prices',
                title: 'Purchase Item Prices',
                moduleId: PLATFORM.moduleName('pages/quote-setup/purchase-item-prices/index')
            },
            {
                route: 'standard-modifiers',
                name: 'standard-modifiers',
                title: 'Standard Modifiers',
                moduleId: PLATFORM.moduleName('pages/standard-setup/standard-modifiers/index')
            },
            {
                route: 'standard-setup/standards',
                name: 'standards',
                title: 'Standards',
                moduleId: PLATFORM.moduleName('pages/standard-setup/standards/index')
            },
            {
                route: 'standard-setup/standards/:id/configuration',
                name: 'standard-configs',
                title: 'Standard Configs',
                parent: 'standards',
                moduleId: PLATFORM.moduleName('pages/standard-setup/standards/configuration/index')
            },
            {
                route: 'quote-setup/bill-of-material',
                name: 'bill-of-material',
                title: 'Bill of Material Master',
                moduleId: PLATFORM.moduleName('pages/quote-setup/bill-of-material/index')
            },
            {
                route: 'quote-setup/bill-of-material/:id',
                name: 'bill-of-material-item',
                title: 'Bill Of Material Item',
                parent: 'bill-of-material',
                moduleId: PLATFORM.moduleName('pages/quote-setup/bill-of-material/item/index')
            },
            {
                route: 'sga-profit-freeze',
                name: 'sga-profit-freeze',
                title: 'SGA & Profit Freeze',
                parent: 'import',
                moduleId: PLATFORM.moduleName('pages/utilities/imports/sga-profit-freeze/index')
            },
            {
                route: 'where-used',
                name: 'where-used',
                title: 'Where Used',
                parent: 'import',
                moduleId: PLATFORM.moduleName('pages/where-used/index')
            },
            {
                route: 'not-found',
                name: 'not-found',
                title: 'Not Found',
                moduleId: PLATFORM.moduleName('pages/not-found/index')
            },
            {
                route: 'tools/user-manager',
                name: 'user-manager',
                title: 'User Manager',
                moduleId: PLATFORM.moduleName('pages/utilities/tools/user-manager/index')
            }
        ]);
    }
}
